import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnInit,
  output,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom, take } from 'rxjs';

import { DEFAULT_MAP_COORDS, MapCoords, MapType } from '../../map.models';

export interface MapOptions {
  enableZoom: boolean;
  mapType: MapType;
}

@Component({
  selector: 'be-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleMapComponent implements OnInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);

  mapId = input<string>();
  options = input<MapOptions>({
    enableZoom: true,
    mapType: MapType.Road,
  });
  zoom = input(8);
  center = input<MapCoords>(DEFAULT_MAP_COORDS);
  mapInitialized = output<google.maps.Map>();
  private mapReadyPromise: Promise<google.maps.Map> | undefined;

  googleMap: google.maps.Map | undefined;
  mapElement: HTMLElement | null = null;

  constructor() {
    effect(() => {
      this.googleMap?.setZoom(this.zoom());
    });

    effect(() => {
      this.googleMap?.setCenter(this.center());
    });

    effect(() => {
      const opts = this.options();
      this.googleMap?.setOptions({
        gestureHandling: opts.enableZoom ? 'greedy' : 'none',
        zoomControl: opts.enableZoom,
        mapTypeId: opts.mapType,
      });
    });
  }

  ngOnInit(): void {
    this.mapElement = this.elementRef.nativeElement.querySelector('.map-container');

    if (google.maps.Map) {
      this.initialize(google.maps.Map);
    } else {
      this.ngZone.runOutsideAngular(() => {
        google.maps.importLibrary('maps').then((lib) => {
          this.initialize((lib as google.maps.MapsLibrary).Map);
        });
      });
    }
  }

  resolveMap(): Promise<google.maps.Map> {
    if (this.googleMap) {
      return Promise.resolve(this.googleMap);
    }

    this.mapReadyPromise ??= firstValueFrom(outputToObservable(this.mapInitialized).pipe(take(1)));

    return this.mapReadyPromise;
  }

  private initialize(mapConstructor: typeof google.maps.Map): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.mapElement) {
        throw new Error('No map element found');
      }

      this.googleMap = new mapConstructor(this.mapElement, {
        clickableIcons: false,
        zoom: this.zoom(),
        center: this.center(),
        mapId: this.mapId(),
        mapTypeControl: false,
        ...this.options(),
      });
      this.mapInitialized.emit(this.googleMap);
    });
  }
}
